import type {
  Driver,
  TaskOutputReferenceInput,
  WorkcellInstrumentConfig,
} from '@automata/wfb-api';
import {
  AdditionalDataInputBooleanInput,
  AdditionalDataInputFloatInput,
  AdditionalDataInputIntegerInput,
  AdditionalDataInputStringInput,
  ComparisonInput,
  ParameterReferenceInput,
  ReferenceDataInput,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';
import type { Connection, Edge, Node } from 'reactflow';

import {
  MIN_GREATER_THAN_MAX_DURATION,
  NO_COMMON_LABWARE,
  TIME_CONSTRAINT_HANDLE_END,
  TIME_CONSTRAINT_HANDLE_START,
} from './constants';
import type {
  LabwareClosureState,
  LabwareContent,
  LabwareProperties,
  LabwarePropertiesForm,
  LabwareType,
  SingleOrMultiUse,
} from './zodSchemas/labwareProperties';
import {
  DataMode,
  TaskParameterValue,
  TaskProperties,
  TaskPropertiesForm,
  TaskType,
} from './zodSchemas/taskProperties';

/** Generic type util to map all values of type T to type X */
export type MapToX<T, X> = {
  [_K in keyof T]: X;
};
export interface LabwareDataForTask {
  id: string;
  colorIndex: number;
  labwareName: string; // TODO IMMEDIATELY rename to just "nickname"
}

export type LabwareDataForTaskByHandlePairId = {
  [handlePairId: string]: LabwareDataForTask | null;
};

export type TimeEstimateType =
  | 'durationBased'
  | 'userEstimated'
  | 'driverSpecified';

export type TaskTimeEstimate = { userEstimate: number } | null;

export enum PropertiesType {
  Action = 'action',
  Code = 'code',
  DataExport = 'dataExport',
  Conditional = 'condition',
  ContinuousLoading = 'continuousLoading',
}

export type CodeTaskProperties = Omit<
  TaskProperties,
  | 'userTimeEstimate'
  | 'instrumentId'
  | 'taskParameters'
  | 'propertiesType'
  | 'labwareIdsToUnitsConsumed'
> & {
  scriptName: string;
  propertiesType: PropertiesType.Code;
  inputDataMode?: DataMode;
  taskParameters?: Record<string, TaskParameterValue> | null;
  taskAppliedParameters?: Record<string, string> | null;
};

export type DataExportTaskProperties = Omit<
  TaskProperties,
  | 'userTimeEstimate'
  | 'instrumentId'
  | 'taskParameters'
  | 'propertiesType'
  | 'labwareIdsToUnitsConsumed'
> & {
  connectorType?: string;
  propertiesType: PropertiesType.DataExport;
  taskParameters?: Record<string, TaskParameterValue> | null;
  taskAppliedParameters?: Record<string, string> | null;
};

// rhs type is too complex to work with slice, so we create a local version
type ComparisonInputLocal = Omit<ComparisonInput, 'rhs'> & {
  rhs:
    | TaskOutputReferenceInput
    | ReferenceDataInput
    | ParameterReferenceInput
    | { value: any };
};

export type ConditionalTaskProperties = Omit<
  TaskProperties,
  | 'userTimeEstimate'
  | 'instrumentId'
  | 'taskParameters'
  | 'propertiesType'
  | 'labwareIdsToUnitsConsumed'
> & {
  comparison: ComparisonInputLocal;
  elseLabel: string;
  ifLabel: string;
  propertiesType: PropertiesType.Conditional;
  expectedOutcome: 'IF condition' | 'ELSE condition';
};

export type ContinuousLoadingProperties = Pick<
  TaskProperties,
  'id' | 'nickname'
> & {
  propertiesType: PropertiesType.ContinuousLoading;
  taskType: 'continuousLoading';
  isLabwareLessTask?: boolean;
  description?: string;
  additionalDataInputs?: AdditionalDataInput[];
};

export type AllTaskProperties =
  | TaskProperties
  | CodeTaskProperties
  | DataExportTaskProperties
  | ConditionalTaskProperties
  | ContinuousLoadingProperties;

export type CodeTaskPropertiesForm = {
  id: string;
  nickname: string;
  scriptName: string;
  inputDataMode?: DataMode;
  taskParameters?: Record<string, TaskParameterValue> | null;
  taskAppliedParameters?: Record<string, string> | null;
};

export type ExportDataTaskPropertiesForm = {
  id: string;
  nickname: string;
  connectorType: string;
  taskParameters?: Record<string, TaskParameterValue> | null;
  taskAppliedParameters?: Record<string, string> | null;
};

export type UpdateTaskParametersFn = (
  parameterName: string,
  update: string // always a string bc it comes from a ChangeEvent etc
) => void;

export type CreateTaskData = {
  properties: AllTaskProperties;
  position: NodePosition;
  handlePairIdOrder?: string[];
  comments?: string;
};

// TODO IMMEDIATELY what is this? Only for display? Name it clearly.
export type InstrumentForLocation = {
  bench: string;
  // TODO(IL, 2024-04-04): TBD if these capabilities based on tasks are desired.
  capabilities: Array<string>;
  capacity: number;
  dataConnectorConfig?: DataConnectorConfig;
  id: string;
  image: string;
  manufacturer: string;
  name: string;
  driverName: string;
  driverVersion: string;
  config: WorkcellInstrumentConfig;
  is_slotless_container?: boolean;
  simulate?: boolean;
  mappings?: string[];
};

export type InstrumentIdsByTaskType = Record<TaskType, string[]>;

export type LabwareNode = Node<null> & { type: 'labware' };
export type TaskNode = Node<null> & { type: 'task' };
export type TimeConstraintNodeData = {
  errors?: TimeConstraintErrorType[];
  labwareCorrespondence: TaskLabwareCorrespondence;
  timeConstraintId: string;
};
export type TimeConstraintNode = Node<TimeConstraintNodeData> & {
  type: 'timeConstraint';
};
export enum NodeType {
  LABWARE = 'labware',
  TASK = 'task',
  CONDITIONAL_TASK = 'conditionalTask',
}
export type GraphNodes = Array<LabwareNode | TaskNode | TimeConstraintNode>;

export type GraphEdges = Edge[]; // TODO IMMEDIATELY: rename LabwareDependencyEdge, use type: typeof LABWARE_DEPENDENCY_EDGE

export type TimeConstraintErrorType =
  | typeof MIN_GREATER_THAN_MAX_DURATION
  | typeof NO_COMMON_LABWARE;

export type TimeConstraintErrorsById = {
  [id: string]: TimeConstraintErrorType[] | undefined;
};

export type ConditionTaskOperator =
  | '=='
  | '!='
  | '<'
  | '<='
  | '>'
  | '>='
  | 'in'
  | 'not in';

export type ConditionTaskDataSource = {
  taskId: string;
  handlePairId: string;
  value: string;
};
// TODO(IL, LT, 2024-08-07): come up with a better word than correspondence??
export type TaskLabwareCorrespondence =
  /** at least one labware in common */
  | 'COMMON_LABWARE'
  /** no labware in common, at least one task has some labware */
  | 'DIFFERENT_LABWARE'
  /** neither task has any labware assigned */
  | 'NO_LABWARE_ASSIGNED';

export type TaskLabwareCorrespondenceById = Record<
  string,
  TaskLabwareCorrespondence
>;

export type TimeConstraintEdgeData = {
  errors?: TimeConstraintErrorType[];
  labwareCorrespondence: TaskLabwareCorrespondence;
  maxDuration: number | null;
  minDuration: number | null;
  iconPosition?: NodePosition;
  onMidpointChange?: (
    timeConstraintId: string,
    position: { x: number; y: number }
  ) => void;
};
export type TimeConstraintEdge = Edge<TimeConstraintEdgeData>;

export type TimeConstraintHandleType =
  | typeof TIME_CONSTRAINT_HANDLE_START
  | typeof TIME_CONSTRAINT_HANDLE_END;
export type TimingTargetInstance = {
  /** Unique ID of the timing target within a connection */
  id: string;
  /** Ideal time between the two anchors (in seconds) */
  target: number;
  /** Priority/weight of the timing target (1-10, default 1) */
  weight: number;
};

export type ConsistencyTargetInstance = {
  /** Unique ID of the consistency target within a connection */
  id: string;
  /** Auto-generated group id used in planning_targets.consistency */
  groupId: string;
};

export type HardTimeConstraintInstance = {
  /** Unique ID of the hard time constraint within a connection */
  id: string;
  /** Minimum duration in seconds, if any */
  minDuration: number | null;
  /** Maximum duration in seconds, if any */
  maxDuration: number | null;
  /** Whether to enable detailed coverage calculation for this rule */
  enableDetailedCoverageCalculation: boolean;
  /** Whether to automatically ignore this constraint if it is broken during execution */
  autoIgnoreIfBroken: boolean;
};

export type TimeConstraintInstance = {
  /** Time constraint connection ID */
  id: string;
  /** ID of source task node */
  source: string;
  /** Type of source handle */
  sourceType: TimeConstraintHandleType;
  /** ID of target task node */
  target: string;
  /** Type of target handle */
  targetType: TimeConstraintHandleType;
  /** Minimum duration in seconds, if any (legacy single-rule storage) */
  minDuration: number | null;
  /** Maximum duration in seconds, if any (legacy single-rule storage) */
  maxDuration: number | null;
  /** Whether to enable detailed coverage calculation (legacy single-rule storage) */
  enableDetailedCoverageCalculation: boolean;
  /** Whether to automatically ignore this constraint if it is broken (legacy single-rule storage) */
  autoIgnoreIfBroken: boolean;
  /** Optional list of hard time constraints (hard); multiple per connection */
  hardConstraints?: HardTimeConstraintInstance[];
  /** Optional list of timing targets (soft); independent from hard constraints */
  timingTargets?: TimingTargetInstance[];
  /** Optional list of consistency targets (soft); independent from others */
  consistencyTargets?: ConsistencyTargetInstance[];
};

// TODO(IL, 2024-04-15): same as reactflow XYPosition
export interface NodePosition {
  x: number;
  y: number;
}

export type SelectedNode = { nodeType: NodeType; id: string };

/** Represents a slot range or a single slot. Usually an array, like [2, {start:10,end:15}] */
export type ParsedSlot = { start: number; end: number } | number;

export type CreateLabwareData = {
  properties: LabwareProperties;
  position: NodePosition;
  colorIndex?: number;
  comments?: string;
};

// DECK LAYOUT
export type DeckLayoutType = 'carousel' | 'grid';

export type DeckLayoutData =
  | { deckLayoutType: 'carousel'; slots: number }
  | { deckLayoutType: 'grid'; columns: number; rows: number };

export type LoadOrderGridLayout =
  | 'firstToLast'
  | 'lastToFirst'
  | 'custom'
  | 'any';

export type LoadOrderCarouselLayout =
  | 'clockwise'
  | 'counterclockwise'
  | 'custom'
  | 'any';

export interface LabwareLayoutData {
  id: string;
  slot: number;
  loadOrder: number;
  unloadOrder: number;
}

// =====

/** a set of entities that have been selected/deselected */
export interface SelectionUpdate {
  selected: string[];
  deselected: string[];
}

export type DataConnectorConfig = {
  inputActions?: string[];
  outputActions?: string[];
};

export type Option<TId = string, TName = string> = {
  id: TId;
  name: TName;
};

export type DriverByTaskId = {
  [taskId: string]: Driver;
};

export type TabId = 'build' | 'preview';

export type NonNullishConnection = {
  // This is like Connection, but none of the values can be `null`
  [K in keyof Connection]: Exclude<Connection[K], null>;
};

// Helper union type for Additional Data Inputs
export type AdditionalDataInput =
  | AdditionalDataInputStringInput
  | AdditionalDataInputIntegerInput
  | AdditionalDataInputFloatInput
  | AdditionalDataInputBooleanInput;

// TODO(IL, 2025-02-19): refactor all the imports to more direct source(s)
// instead of redirecting through this types.ts file
export type {
  AdditionalDataInputBooleanInput,
  AdditionalDataInputFloatInput,
  AdditionalDataInputIntegerInput,
  AdditionalDataInputStringInput,
  LabwareClosureState,
  LabwareContent,
  LabwareProperties,
  LabwarePropertiesForm,
  LabwareType,
  SingleOrMultiUse,
  TaskParameterValue,
  TaskProperties,
  TaskPropertiesForm,
  TaskType,
};
